/**
 * Maps route path to help document slug. Used by the header Help button and HelpDrawer.
 */
import {
  flattenNavigationRoutes,
  getNavigation,
} from "../config/navigationConfig";

const exactHelpRoutes = [
  {
    path: "/dashboard",
    match: (p) => p === "/dashboard",
    slug: "dashboard",
    label: "Dashboard",
  },
  {
    path: "/bridges",
    match: (p) => p === "/bridges",
    slug: "bridges",
    label: "Bridges",
  },
  {
    path: "/bridges/:id",
    match: (p) => p.startsWith("/bridges/") && !p.includes("/repair"),
    slug: "bridge-detail",
    label: "Bridge Detail",
  },
  {
    path: "/enhanced-simulation-results",
    match: (p) => p === "/enhanced-simulation-results",
    slug: "simulation-engine",
    label: "Simulation Engine",
  },
  {
    path: "/repair-projects",
    match: (p) => p === "/repair-projects",
    slug: "repair-projects",
    label: "Repair Projects",
  },
  {
    path: "/repair-projects/create",
    match: (p) => p === "/repair-projects/create",
    slug: "repair-projects-create",
    label: "Create Repair Project",
  },
  {
    path: "/repair-projects/:id",
    match: (p) => p.startsWith("/repair-projects/"),
    slug: "repair-projects-detail",
    label: "Repair Project Details",
  },
  {
    path: "/scenarios",
    match: (p) => p === "/scenarios",
    slug: "scenarios",
    label: "Scenarios",
  },
  {
    path: "/span-simulation",
    match: (p) => p === "/span-simulation",
    slug: "span-simulation",
    label: "Span Simulation",
  },
  {
    path: "/repair-planning",
    match: (p) => p === "/repair-planning",
    slug: "repair-planning",
    label: "Repair Planning",
  },
  {
    path: "/masters/corrosion-damage",
    match: (p) => p === "/masters/corrosion-damage",
    slug: "masters-corrosion-damage",
    label: "Corrosion and Damage",
  },
  {
    path: "/masters/repair-rules",
    match: (p) => p === "/masters/repair-rules",
    slug: "masters-repair-rules",
    label: "Repair Rules",
  },
  {
    path: "/masters/decay-trigger-config",
    match: (p) => p === "/masters/decay-trigger-config",
    slug: "masters-decay-trigger-config",
    label: "Decay Trigger Config",
  },
  {
    path: "/masters/new-simulation-triggers",
    match: (p) => p === "/masters/new-simulation-triggers",
    slug: "masters-new-simulation-triggers",
    label: "Simulation Triggers",
  },
  {
    path: "/masters/maintenance-cycle-limits",
    match: (p) => p === "/masters/maintenance-cycle-limits",
    slug: "masters-maintenance-cycle-limits",
    label: "Maintenance Cycle Limits",
  },
  {
    path: "/masters/surface-prep-methods",
    match: (p) => p === "/masters/surface-prep-methods",
    slug: "masters-surface-prep-methods",
    label: "Surface Prep Methods",
  },
  {
    path: "/masters/washing-costs",
    match: (p) => p === "/masters/washing-costs",
    slug: "masters-washing-costs",
    label: "Washing Costs",
  },
  {
    path: "/masters/wash-rules",
    match: (p) => p === "/masters/wash-rules",
    slug: "masters-wash-rules",
    label: "Wash Rules",
  },
  {
    path: "/masters/materials-master",
    match: (p) => p === "/masters/materials-master",
    slug: "masters-materials-master",
    label: "Paints Master",
  },
  {
    path: "/masters/mobilization-costs",
    match: (p) => p === "/masters/mobilization-costs",
    slug: "masters-mobilization-costs",
    label: "Mobilization Costs",
  },
  {
    path: "/masters/field-application-labor",
    match: (p) => p === "/masters/field-application-labor",
    slug: "masters-field-application-labor",
    label: "Field Application Labor",
  },
  {
    path: "/masters/labor-by-coat",
    match: (p) => p === "/masters/labor-by-coat",
    slug: "masters-labor-by-coat",
    label: "Labor by Coat Type",
  },
  {
    path: "/masters/system-settings",
    match: (p) => p === "/masters/system-settings",
    slug: "masters-system-settings",
    label: "System Settings",
  },
  {
    path: "/users",
    match: (p) => p === "/users",
    slug: "user-management",
    label: "User Management",
  },
  {
    path: "/settings",
    match: (p) => p === "/settings",
    slug: "settings",
    label: "Settings",
  },
  {
    path: "/profile",
    match: (p) => p === "/profile",
    slug: "profile",
    label: "Profile",
  },
  {
    path: "/help",
    match: (p) => p === "/help" || p === "/help.html",
    slug: "help-center",
    label: "Help Center",
  },
];

const pathToSlug = (path) =>
  (path || "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[:]/g, "")
    .replace(/\//g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();

const buildNavHelpRoutes = () => {
  const navigation = getNavigation({ isAdmin: true, isPlanner: false });
  const navRoutes = flattenNavigationRoutes(navigation)
    .map((item) => ({
      path: item.href,
      label: item.name,
      slug: pathToSlug(item.href),
      match: (p) => p === item.href,
    }))
    .filter((item) => item.path && item.path.startsWith("/"));

  return navRoutes;
};

// Keep explicit route mappings first (especially for dynamic/detail pages)
// and auto-add any missing navigation routes from the shared sidebar config.
const helpRoutes = (() => {
  const byPath = new Map(exactHelpRoutes.map((route) => [route.path, route]));
  const navRoutes = buildNavHelpRoutes();

  navRoutes.forEach((route) => {
    if (!byPath.has(route.path)) {
      byPath.set(route.path, route);
    }
  });

  return Array.from(byPath.values());
})();

export const getHelpSlugForPath = (path) => {
  const normalized = path || "";
  const match = helpRoutes.find((route) => route.match(normalized));
  return match ? match.slug : null;
};

export const getHelpRouteOptions = () =>
  helpRoutes.map((r) => ({ label: r.label, slug: r.slug, path: r.path }));
export const helpRouteList = helpRoutes;
